import { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { FieldValues, useForm } from "react-hook-form";

import { type TalentBrowseProps, type TalentSelection } from "@/components/talentBrowse";
import { createBooker } from "../../services/bookers";
import { analyticsService } from "../../services/analytics";
import { useExchangeRates } from "../../services/exchangeRates";
import { UserContext, useFreshLists, useFreshBookers, useFreshClients, useFreshTalent, useFreshCountries, getCampaignsByBookerId, newCreateOrUpdateCampaign, acceptPaymentTerms, useFreshCities, useRelevantDocs, useFreshSalesTeam, warmFullTalent } from "../../services/firebase";
import { contentTags } from "../../shared/grailConstants";
import { asPlatforms } from "../../shared/platforms";
import { BrowseFilter, Talent, User, SelectedTalent, CampaignType, List, Platform } from "../../shared/types";
import { buildListSwitcherOptions } from "../../utils/lists";
import { LIST_ORDER_SORT_BY } from "../../utils/orderedTalentIds";
import { buildCampaignAndBookings, calculateTotals, canContinueToBookingForm, getInitialState, listLocksCampaignPlatform, newBookingStateReducer } from "./booking";

/**
 * Queries, reducer, browse wiring, and submit for New Booking.
 * Returns the props each step renders.
 */
export function useNewBooking() {
    const { listId } = useParams<{ listId: string }>();
    const reactHookForm = useForm();
    const authenticatedUser = useContext(UserContext);
    const queryClient = useQueryClient();
    const exchangeRates = useExchangeRates();

    const listsQuery = useFreshLists({ listId });
    const listData = listsQuery.flatData;

    const bookersQuery = useFreshBookers({ enabled: Boolean(authenticatedUser?.roles?.manager || authenticatedUser?.roles?.admin) });
    const bookers = bookersQuery.flatData;
    const clientsQuery = useFreshClients({ enabled: Boolean(authenticatedUser?.roles?.manager || authenticatedUser?.roles?.admin) });
    const clients = clientsQuery.flatData;
    const salesTeamQuery = useFreshSalesTeam({ enabled: Boolean(authenticatedUser?.roles?.manager || authenticatedUser?.roles?.admin) });
    const salesTeam = salesTeamQuery.flatData;

    const countriesQuery = useFreshCountries();
    const countryList = countriesQuery.flatData.filter((country) => country.liveOnAirtable).map((country) => country.label).sort();
    const tagList = contentTags;
    const cityQuery = useFreshCities();
    const cityList = cityQuery.flatData.map((cityDoc) => cityDoc.city).sort();

    const listTalentIds = listId ? Object.keys(listData[0]?.talent || {}) : [];
    const listTalentQuery = useRelevantDocs<Talent>({ collectionName: "talent", docIds: listTalentIds, enabled: Boolean(listTalentIds?.length) });
    const listTalent = listTalentQuery.data;

    // Bookers default to All (`filter.listId` is `""`). That path already mounts `fullTalentQuery` on the unscoped key.
    // Enabling this hook for a booker would walk the same 19k catalog twice in one frame (`2 * SLICE_MS`).
    const partialTalentQuery = useFreshTalent({
        enabled: Boolean(!listId && authenticatedUser?.roles?.manager && authenticatedUser.id),
        ...(authenticatedUser?.roles?.manager && { managerId: authenticatedUser.id }),
        ...(authenticatedUser?.roles?.superAdmin && { superAdmin: true })
    });
    const partialTalent = partialTalentQuery.flatData;

    const [state, dispatch] = useReducer(newBookingStateReducer, getInitialState({ user: authenticatedUser, listId }));
    const { booker, totalPrice, totalTiktokFollowers, totalInstagramFollowers, step, selectedTalent, showMobileSummary, filter, platforms, submissionStatus, creedModalOpen } = state;
    const { currency, campaignType } = filter;
    const isManager = Boolean(authenticatedUser?.roles?.manager);
    const userId = authenticatedUser?.id;
    // Browse roster/list first. Attach the full catalog only after the switcher leaves My Roster.
    const isFullList = filter.listId !== "my-roster";
    // Latch: going back to My Roster must not drop All-list picks from summary / totals / submit (they resolve against rawTalent).
    const [hasSelectedFullList, setHasSelectedFullList] = useState(isFullList);
    if (isFullList && !hasSelectedFullList) {
        setHasSelectedFullList(true);
    }

    const fullTalentQuery = useFreshTalent({
        enabled: isFullList || hasSelectedFullList,
        ...(authenticatedUser?.roles?.superAdmin && { superAdmin: true })
    });

    // Fetch the catalog into RQ/IDB without mounting the hook, so All can attach from cache. Does not price/filter.
    useEffect(() => {
        if (!authenticatedUser?.id) return;
        void warmFullTalent(queryClient);
    }, [authenticatedUser?.id, queryClient]);

    const rawTalent = (isFullList || hasSelectedFullList) && fullTalentQuery.flatData.length
        ? fullTalentQuery.flatData
        : listTalent || partialTalent;

    const loadingMore =
        !rawTalent.length && (
            listId
                ? (
                    listsQuery.query.isFetching ||
                    listsQuery.query.isLoading ||
                    (listTalentIds.length > 0 && (listTalentQuery.isFetching || listTalentQuery.isLoading))
                )
                : (
                    (isFullList || hasSelectedFullList)
                        ? (fullTalentQuery.isFetching || fullTalentQuery.isLoading)
                        : (partialTalentQuery.isFetching || partialTalentQuery.isLoading)
                )
        );

    useEffect(() => {
        if (listId && listData[0]) {
            const singleList = listData[0];
            const listPlatforms = asPlatforms(singleList.platform ?? undefined);
            const filterPartial: Partial<BrowseFilter> = {
                ...(listPlatforms.length ? { lockCampaignPlatform: true } : {}),
                ...(singleList.campaignType && { campaignType: singleList.campaignType, lockCampaignType: true }),
                ...(singleList.currency && { currency: singleList.currency, lockCurrencyFromList: true }),
                listId: singleList.id,
            };

            dispatch({ type: "updateFilter", filterPartial });
            if (listPlatforms.length) {
                dispatch({ type: "setStatePartial", payload: { platforms: listPlatforms } });
            }
        }
    }, [listId, listData]);

    const lists: List[] = useMemo(() => {
        if (listId) {
            return listData[0] ? [listData[0]] : [];
        }
        return buildListSwitcherOptions({
            userId: isManager ? userId : undefined,
            rawTalent,
            systemLists: listData,
            campaignType: campaignType || "Song",
        });
    }, [listId, listData, isManager, userId, rawTalent, campaignType]);

    useEffect(() => {
        if (booker?.id) {
            getCampaignsByBookerId(booker.id).then((campaigns) => dispatch({ type: "setStatePartial", payload: { campaigns } }));
        } else {
            dispatch({ type: "setStatePartial", payload: { campaigns: [] } });
        }
    }, [booker?.id]);

    async function submitBooking(formData: FieldValues) {
        if (!canContinueToBookingForm({ booker, selectedTalent, platforms })) {
            return;
        }
        dispatch({ type: "navigateToStep", step: "confirmation" });

        const campaignAndNewBookings = await buildCampaignAndBookings(formData, authenticatedUser, state, rawTalent, salesTeam);
        const { campaign, bookings } = campaignAndNewBookings;

        const file: unknown = formData.file;
        const campaignRes = await newCreateOrUpdateCampaign(campaign, bookings, file instanceof FileList ? file : undefined);
        if (formData.paymentTerms?.termsAccepted) {
            try {
                await acceptPaymentTerms({ firstName: booker?.firstName, lastName: booker?.lastName, userId: booker?.id, clientId: booker?.clientId });
            } catch {
                // keep going — terms write is best-effort
            }
        }

        dispatch({ type: "setSubmissionStatus", status: campaignRes });
        const campaignField: unknown = formData.campaign;
        const hadExistingCampaign = campaignField !== null && typeof campaignField === "object" && "id" in campaignField && typeof campaignField.id === "string" && Boolean(campaignField.id);
        analyticsService().logEvent(hadExistingCampaign ? "Updated Campaign" : "Created Campaign", { ...campaign, bookings: JSON.stringify(bookings) });
    }

    async function selectBooker(next?: User) {
        if ((next && next.id) || !next) {
            dispatch({ type: "setBooker", booker: next });
        } else {
            const res = await createBooker(next, authenticatedUser);

            if (res.success) {
                queryClient.refetchQueries({ queryKey: ["bookers"] });
                dispatch({ type: "setBooker", booker: { ...next, id: res.result, clientId: res.clientId } });
            } else {
                alert(`${res.error} - ${next.email} - Please contact Lyonel and Tim and copy and paste this message.`);
            }
        }
    }

    const hasSelectedTalent = Object.keys(selectedTalent || {}).length > 0;
    const platformDisabled = Boolean(filter.lockCampaignPlatform);
    const platformTooltip = filter.lockCampaignPlatform
        ? `List locked to ${platforms.join(", ") || "this list"}.`
        : "";
    const selected: TalentSelection = useMemo(() => {
        const record: TalentSelection = {};
        for (const [id, item] of Object.entries(selectedTalent)) {
            if (typeof item.price === "number") {
                record[id] = { price: item.price };
            }
        }
        return record;
    }, [selectedTalent]);

    function handleSelectionChange(next: TalentSelection) {
        const merged: SelectedTalent = {};
        for (const [id, entry] of Object.entries(next)) {
            merged[id] = { ...selectedTalent[id], price: entry.price };
        }
        const prevIds = new Set(Object.keys(selectedTalent));
        const nextIds = new Set(Object.keys(merged));
        for (const id of nextIds) {
            if (prevIds.has(id)) {
                continue;
            }
            const tal = rawTalent.find((item) => item.id === id);
            analyticsService().logEvent("Selected Talent", {
                talentId: id,
                tiktokUser: tal?.platformData?.TikTok?.username,
                instagramUser: tal?.platformData?.Instagram?.username,
                bidPrice: merged[id].price,
                recommendation: merged[id].isRec || false,
            });
        }
        for (const id of prevIds) {
            if (nextIds.has(id)) {
                continue;
            }
            const tal = rawTalent.find((item) => item.id === id);
            analyticsService().logEvent("Deselected Talent", {
                talentId: id,
                tiktokUser: tal?.platformData?.TikTok?.username,
                instagramUser: tal?.platformData?.Instagram?.username,
            });
        }
        dispatch({ type: "setStatePartial", payload: { selectedTalent: merged, ...calculateTotals(merged, rawTalent) } });
    }

    function handleRemove(id: string) {
        const merged: SelectedTalent = { ...selectedTalent };
        delete merged[id];
        const tal = rawTalent.find((item) => item.id === id);
        analyticsService().logEvent("Deselected Talent", {
            talentId: id,
            tiktokUser: tal?.platformData?.TikTok?.username,
            instagramUser: tal?.platformData?.Instagram?.username,
        });
        dispatch({ type: "setStatePartial", payload: { selectedTalent: merged, ...calculateTotals(merged, rawTalent) } });
    }

    function applyPlatforms(nextPlatforms: Platform[]) {
        dispatch({ type: "setStatePartial", payload: { platforms: nextPlatforms } });
    }

    function togglePlatform(platform: Platform, checked: boolean) {
        const nextPlatforms = checked
            ? [...platforms, platform]
            : platforms.filter((item) => item !== platform);
        applyPlatforms(nextPlatforms);
    }

    function onListIdChange(nextListId: string) {
        const list = lists.find((item) => item.id === nextListId);
        const listPlatforms = asPlatforms(list?.platform ?? undefined);
        const lockCampaignPlatform = listLocksCampaignPlatform(list);
        dispatch({
            type: "updateFilter",
            filterPartial: {
                listId: nextListId,
                lockCampaignPlatform,
            },
        });
        if (lockCampaignPlatform && listPlatforms.length) {
            dispatch({ type: "setStatePartial", payload: { platforms: listPlatforms } });
        }
    }

    return {
        step,
        creed: {
            open: creedModalOpen,
            onOpenChange: (open: boolean) => {
                if (!open) dispatch({ type: "setStatePartial", payload: { creedModalOpen: false } });
            },
        },
        campaignConstraints: {
            booker: isManager ? {
                isManager: true,
                value: booker,
                bookers,
                clients,
                onSelect: selectBooker,
                disabled: hasSelectedTalent,
            } : undefined,
            currency: {
                value: currency,
                locked: Boolean(filter.lockCurrencyFromBooker || filter.lockCurrencyFromList),
                disabled: hasSelectedTalent,
                onChange: (value: string | null) => dispatch({ type: "updateFilter", filterPartial: { currency: (value || "USD") as BrowseFilter["currency"] } }),
            },
            platforms: {
                value: platforms,
                disabled: platformDisabled,
                tooltip: platformTooltip,
                onToggle: togglePlatform,
            },
            campaignType: filter.showCampaignTypeSelect ? {
                value: filter.campaignType,
                locked: Boolean(filter.lockCampaignType),
                disabled: hasSelectedTalent,
                onChange: (nextType: CampaignType) => {
                    dispatch({ type: "updateFilter", filterPartial: { campaignType: nextType } });
                },
            } : undefined,
        },
        browse: authenticatedUser ? {
            talent: platforms.length ? rawTalent : [],
            lists,
            platforms,
            campaignType: campaignType || "Song",
            currency,
            exchangeRates,
            selected,
            onSelectionChange: handleSelectionChange,
            defaultListId: filter.listId,
            ...(listId ? { defaultSortBy: LIST_ORDER_SORT_BY } : {}),
            onListIdChange,
            filterSheet: {
                showPriceChips: true,
                currency,
                countryList,
                tagList,
                cityList,
            },
            rowState: filter.lockTalentCards ? () => ({ selectable: false }) : undefined,
        } satisfies TalentBrowseProps : undefined,
        loadingMore,
        form: {
            booker,
            filter: { campaignType: filter.campaignType },
            campaigns: state.campaigns,
            platforms,
            reactHookForm,
            salesTeam,
            onSubmit: reactHookForm.handleSubmit(submitBooking),
        },
        confirmation: {
            status: submissionStatus,
            isManager,
        },
        summary: {
            talent: rawTalent,
            selectedTalent,
            userCurrency: filter.currency,
            totalPrice,
            totalTiktokFollowers,
            totalInstagramFollowers,
            handleRemove,
            showMobileSummary,
        },
        nav: {
            bookingStep: step,
            navigateToStep: (next: typeof step) => {
                if (next === "form" && !canContinueToBookingForm({ booker, selectedTalent, platforms })) {
                    return;
                }
                dispatch({ type: "navigateToStep", step: next });
            },
            showMobileSummary,
            setShowMobileSummary: (show: boolean) => dispatch({ type: "setStatePartial", payload: { showMobileSummary: show } }),
            selectedTalent,
            booker,
            hasPlatforms: platforms.length > 0,
            formSubmitDisabled: reactHookForm.formState.isSubmitting,
        },
    };
}
